// ═══════════════════════════════════════════════════════════════
// use-feedback — beta feedback and feature requests.
//
// The beta's whole bargain is that people tell you what's wrong and
// what's missing. That only works if filing something takes fifteen
// seconds from inside the app; anything that requires opening email
// gets thought about and then not done.
//
// Every submission carries diagnostic context the user would never
// think to include — build, screen, device — which is usually the
// difference between "it broke" and a reproducible bug.
// ═══════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import {
  addDoc, collection, doc, onSnapshot, orderBy, query, updateDoc,
  serverTimestamp, where,
} from 'firebase/firestore';
import { db, auth, IS_SANDBOX } from '../firebase.js';
import { APP_VERSION } from '../version.js';

export const FEEDBACK_KINDS = [
  { value: 'feature', label: 'Feature request', hint: 'Something the app should do' },
  { value: 'bug',     label: 'Something is broken', hint: "It doesn't work the way it should" },
  { value: 'confusing', label: 'Confusing', hint: "I couldn't work out how to do something" },
  { value: 'other',   label: 'Something else', hint: 'Anything at all' },
];

export const FEEDBACK_STATUSES = [
  { value: 'new',       label: 'New' },
  { value: 'planned',   label: 'Planned' },
  { value: 'shipped',   label: 'Shipped' },
  { value: 'declined',  label: 'Not doing' },
];

export function feedbackKindLabel(v) {
  return FEEDBACK_KINDS.find((k) => k.value === v)?.label || 'Feedback';
}
export function feedbackStatusLabel(v) {
  return FEEDBACK_STATUSES.find((s) => s.value === v)?.label || 'New';
}

export function feedbackCol() {
  return collection(db, 'feedback');
}

// File a piece of feedback. The context block is attached here rather
// than asked for — a mechanic reporting a bug shouldn't have to know
// what a build number is.
export async function submitFeedback({ kind, title, detail }) {
  const user = auth.currentUser;
  if (!user) throw new Error('You need to be signed in to send feedback.');
  const clean = (title || '').trim();
  if (!clean) throw new Error('Give it a short title so it can be found again.');

  return addDoc(feedbackCol(), {
    userId: user.uid,
    userEmail: user.email || '',
    userName: user.displayName || '',
    kind: FEEDBACK_KINDS.some((k) => k.value === kind) ? kind : 'other',
    title: clean.slice(0, 140),
    detail: (detail || '').trim().slice(0, 4000),
    status: 'new',
    // Context the reporter would never think to include.
    appVersion: APP_VERSION,
    env: IS_SANDBOX ? 'sandbox' : 'prod',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
    createdAt: serverTimestamp(),
  });
}

// Everything filed, newest first. Admin-only by the rules — a
// non-admin subscription simply errors and returns empty.
export function useAllFeedback() {
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(feedbackCol(), orderBy('createdAt', 'desc')),
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setReady(true);
      },
      (err) => {
        console.error('Feedback load failed:', err);
        setError(err);
        setReady(true);
      }
    );
    return unsub;
  }, []);

  return { items, ready, error };
}

// What the signed-in user has filed themselves, so the beta doesn't
// feel like shouting into a void.
export function useMyFeedback() {
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setReady(true); return; }
    const unsub = onSnapshot(
      query(feedbackCol(), where('userId', '==', uid)),
      (snap) => {
        setItems(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
        );
        setReady(true);
      },
      (err) => {
        console.error('My feedback load failed:', err);
        setReady(true);
      }
    );
    return unsub;
  }, []);

  return { items, ready };
}

export async function setFeedbackStatus(id, status) {
  await updateDoc(doc(feedbackCol(), id), {
    status,
    updatedAt: serverTimestamp(),
  });
}
